import { logSupabaseException } from "@punchup/sentry";
import {
  SpecialComedianTag,
  SpecialComedianTagWithComedian,
  SpecialTagStatus,
} from "../../models/special-tags";
import { createSupabaseAdminClient } from "../../supabase/supabase";
import dayjs from "@punchup/utils/dayjs";

// Batch version: fetches accepted tags for multiple specials in a single query.
// Returns a Map keyed by special_id.
export async function getAcceptedTagsForSpecials(
  specialIds: string[]
): Promise<Map<string, SpecialComedianTagWithComedian[]>> {
  if (specialIds.length === 0) return new Map();

  const supabase = createSupabaseAdminClient();

  const { data, error } = await supabase
    .from("special_comedian_tags")
    .select(
      `
      *,
      comedian:comedians!special_comedian_tags_comedian_id_fkey ( id, display_name, slug, about_img )
    `
    )
    .in("special_id", specialIds)
    .eq("status", "accepted")
    .order("ordering", { ascending: true, nullsFirst: false })
    .order("created_at", { ascending: true });

  if (error) {
    logSupabaseException(
      error,
      "Failed to batch get accepted tags for specials"
    );
    throw error;
  }

  const result = new Map<string, SpecialComedianTagWithComedian[]>();
  for (const row of (data ?? []) as SpecialComedianTagWithComedian[]) {
    const list = result.get(row.special_id) ?? [];
    list.push(row);
    result.set(row.special_id, list);
  }
  return result;
}

// Returns only accepted tags; uses admin client since RLS policies are dropped on this table
export async function getAcceptedTagsForSpecial(
  specialId: string
): Promise<SpecialComedianTagWithComedian[]> {
  const supabase = createSupabaseAdminClient();

  const { data, error } = await supabase
    .from("special_comedian_tags")
    .select(
      `
      *,
      comedian:comedians!special_comedian_tags_comedian_id_fkey ( id, display_name, slug, about_img )
    `
    )
    .eq("special_id", specialId)
    .eq("status", "accepted")
    .order("ordering", { ascending: true, nullsFirst: false })
    .order("created_at", { ascending: true });

  if (error) {
    logSupabaseException(error, "Failed to get accepted tags for special");
    throw error;
  }

  return (data ?? []) as SpecialComedianTagWithComedian[];
}

export async function getTagsForSpecial(
  specialId: string
): Promise<SpecialComedianTagWithComedian[]> {
  const supabase = createSupabaseAdminClient();

  const { data, error } = await supabase
    .from("special_comedian_tags")
    .select(
      `
      *,
      comedian:comedians!special_comedian_tags_comedian_id_fkey ( id, display_name, slug, about_img )
    `
    )
    .eq("special_id", specialId)
    .order("ordering", { ascending: true, nullsFirst: false })
    .order("created_at", { ascending: true });

  if (error) {
    logSupabaseException(error, "Failed to get tags for special");
    throw error;
  }

  return (data ?? []) as SpecialComedianTagWithComedian[];
}

export async function getPendingTagsForComedian(
  comedianId: string
): Promise<SpecialComedianTagWithComedian[]> {
  const supabase = createSupabaseAdminClient();

  const { data, error } = await supabase
    .from("special_comedian_tags")
    .select(
      `
      *,
      comedian:comedians!special_comedian_tags_comedian_id_fkey ( id, display_name, slug, about_img )
    `
    )
    .eq("comedian_id", comedianId)
    .eq("status", "pending")
    .order("requested_at", { ascending: false });

  if (error) {
    logSupabaseException(error, "Failed to get pending tags for comedian");
    throw error;
  }

  return (data ?? []) as SpecialComedianTagWithComedian[];
}

export async function getAcceptedTagsForComedian(
  comedianId: string
): Promise<SpecialComedianTagWithComedian[]> {
  const supabase = createSupabaseAdminClient();

  const { data, error } = await supabase
    .from("special_comedian_tags")
    .select(
      `
      *,
      comedian:comedians!special_comedian_tags_comedian_id_fkey ( id, display_name, slug, about_img )
    `
    )
    .eq("comedian_id", comedianId)
    .eq("status", "accepted")
    .order("created_at", { ascending: false });

  if (error) {
    logSupabaseException(error, "Failed to get accepted tags for comedian");
    throw error;
  }

  return (data ?? []) as SpecialComedianTagWithComedian[];
}

export async function createSpecialTag(params: {
  specialId: string;
  comedianId: string;
  taggedBy: string;
  ordering?: number;
  status?: SpecialTagStatus;
}): Promise<SpecialComedianTag> {
  const supabase = createSupabaseAdminClient();

  const { data, error } = await supabase
    .from("special_comedian_tags")
    .insert({
      special_id: params.specialId,
      comedian_id: params.comedianId,
      tagged_by: params.taggedBy,
      ordering: params.ordering ?? null,
      ...(params.status ? { status: params.status } : {}),
      ...(params.status && params.status !== "pending"
        ? { resolved_at: dayjs().toISOString() }
        : {}),
    })
    .select("*")
    .single();

  if (error) {
    logSupabaseException(error, "Failed to create special tag");
    throw error;
  }

  return data;
}

export async function updateSpecialTagStatus(
  tagId: string,
  status: SpecialTagStatus
): Promise<SpecialComedianTag> {
  const supabase = createSupabaseAdminClient();

  const { data, error } = await supabase
    .from("special_comedian_tags")
    .update({
      status,
      resolved_at: dayjs().toISOString(),
    })
    .eq("id", tagId)
    .select("*")
    .single();

  if (error) {
    logSupabaseException(error, "Failed to update special tag status");
    throw error;
  }

  return data;
}

export async function deleteSpecialTag(tagId: string): Promise<void> {
  const supabase = createSupabaseAdminClient();

  const { error } = await supabase
    .from("special_comedian_tags")
    .delete()
    .eq("id", tagId);

  if (error) {
    logSupabaseException(error, "Failed to delete special tag");
    throw error;
  }
}

export async function getSpecialTagById(
  tagId: string
): Promise<SpecialComedianTag | null> {
  const supabase = createSupabaseAdminClient();

  const { data } = await supabase
    .from("special_comedian_tags")
    .select("*")
    .eq("id", tagId)
    .maybeSingle()
    .throwOnError();

  return data;
}
